import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { FileVisibility } from "@/lib/enums";
import { fileKeys } from "@/lib/query";

export interface FileRecord {
  id: string;
  key: string;
  thumbnailKey: string | null;
  placeholder: string | null;
  contentType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  uploadedBy: string;
  status: "pending" | "uploaded" | "failed";
  /** `public` = served at its `/f/:id/:name` URL (see `publicFileUrl`). */
  visibility: FileVisibility;
  createdAt: string;
  updatedAt: string;
}

export interface FileDetail extends FileRecord {
  /** Short-lived presigned GET URL for the primary object. */
  url: string;
  /** Short-lived presigned GET URL for the thumbnail, when present. */
  thumbnailUrl: string | null;
}

/**
 * Upload an image through the server pipeline — Bun.Image resizes, converts to
 * WebP, builds a thumbnail, and writes both to storage. Returns the file record.
 */
export function useUploadImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File): Promise<FileRecord> => {
      const body = new FormData();
      body.append("file", file);
      const res = await fetch("/api/files/image", { method: "POST", body });
      if (!res.ok) {
        const message = await res
          .json()
          .then((j: { error?: string }) => j.error)
          .catch(() => null);
        throw new Error(message ?? "Failed to upload image");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
      toast.success("Image uploaded");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to upload image");
    },
  });
}

/** What `POST /api/files/presign` returns: the pending row, and where + how to PUT the bytes. */
export interface PresignedFileUpload {
  id: string;
  key: string;
  url: string;
  /** Send EVERY one with the PUT — Azure Blob refuses a PUT without `x-ms-blob-type`. */
  headers: Record<string, string>;
}

type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

async function errorFrom(res: Response, fallback: string): Promise<Error> {
  const message = await res
    .json()
    .then((j: { error?: string }) => j.error)
    .catch(() => null);
  return new Error(message ?? fallback);
}

/**
 * PUT bytes straight to a signed upload link. The server's `headers` win over
 * anything guessed here: they are what the store requires (Azure's
 * `x-ms-blob-type: BlockBlob`) and the type the object is stored with.
 */
export async function putToSignedUrl(
  upload: { url: string; headers: Record<string, string> },
  file: Blob,
  fetchImpl: FetchLike = fetch,
): Promise<void> {
  const res = await fetchImpl(upload.url, {
    method: "PUT",
    body: file,
    // The link is its own credential; never send this app's cookies to the store.
    credentials: "omit",
    headers: { ...(file.type ? { "Content-Type": file.type } : {}), ...upload.headers },
  });
  // Status only: the link is a credential for this one object and never goes in a message.
  if (!res.ok) throw new Error(`Upload failed (HTTP ${res.status})`);
}

/**
 * The browser upload path for arbitrary files: ask for a signed link, PUT the
 * bytes to storage directly (they never touch our server), then confirm so the
 * row flips `pending → uploaded`.
 */
export async function uploadFileDirect(
  file: File,
  fetchImpl: FetchLike = fetch,
): Promise<FileRecord> {
  const presignRes = await fetchImpl("/api/files/presign", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename: file.name, contentType: file.type, sizeBytes: file.size }),
  });
  if (!presignRes.ok) throw await errorFrom(presignRes, "Failed to start upload");
  const upload: PresignedFileUpload = await presignRes.json();

  await putToSignedUrl(upload, file, fetchImpl);

  const confirmRes = await fetchImpl("/api/files/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: upload.id }),
  });
  if (!confirmRes.ok) throw await errorFrom(confirmRes, "Failed to finish upload");
  const json = await confirmRes.json();
  return json.data;
}

/**
 * Upload an arbitrary file (PDF, CSV, …) with a signed link — bytes go
 * browser → storage. For images, `useUploadImage` runs the resize pipeline.
 */
export function useUploadFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadFileDirect(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
      toast.success("File uploaded");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to upload file");
    },
  });
}

/**
 * Make a file public (served at `/f/:id/:name`) or private again. The file's
 * owner only; the server audits the change.
 */
export async function setFileVisibility(
  fileId: string,
  visibility: FileVisibility,
  fetchImpl: FetchLike = fetch,
): Promise<FileRecord> {
  const res = await fetchImpl(`/api/files/${encodeURIComponent(fileId)}/visibility`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ visibility }),
  });
  if (!res.ok) throw await errorFrom(res, "Failed to change who can see this file");
  const json = await res.json();
  return json.data;
}

export function useSetFileVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ fileId, visibility }: { fileId: string; visibility: FileVisibility }) =>
      setFileVisibility(fileId, visibility),
    onSuccess: (record) => {
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
      toast.success(record.visibility === "public" ? "File is now public" : "File is now private");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to change who can see this file");
    },
  });
}

/** Resolve a file id to its metadata + a fresh presigned download URL. */
/**
 * A URL for an avatar: a direct URL stays as it is, and a bare file id (an avatar uploaded before
 * public files existed) resolves through `useFile`. The shell's profile block takes only URLs.
 */
export function useAvatarUrl(image: string | null): string | null {
  const direct =
    image !== null &&
    (image.startsWith("http") || image.startsWith("/") || image.startsWith("data:"));
  const file = useFile(direct ? null : image).data;
  return direct ? image : (file?.url ?? null);
}

export function useFile(fileId: string | null) {
  return useQuery({
    queryKey: fileKeys.detail(fileId ?? ""),
    enabled: fileId !== null && fileId !== "",
    queryFn: async (): Promise<FileDetail> => {
      const res = await fetch(`/api/files/${fileId}`);
      if (!res.ok) throw new Error("Failed to fetch file");
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * Delete a file row and its stored objects.
 *
 * Deliberately unwired — the template ships no file-manager UI, but
 * `DELETE /api/files/:id` exists, and this is the client half of it. Keep it:
 * removing it would leave that route unreachable and the next feature would
 * just re-roll it. Knip doesn't flag it because `src/hooks/*.ts` are entries
 * (shipped API surface — see knip.jsonc).
 */
export function useDeleteFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      const res = await fetch(`/api/files/${fileId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
      toast.success("File deleted");
    },
    onError: () => {
      toast.error("Failed to delete file");
    },
  });
}
